/**
 * Security utilities for XSS prevention and input sanitization
 */

/**
 * Sanitize HTML input to prevent XSS attacks
 * @param {string} input - The input string to sanitize
 * @returns {string} - Sanitized string
 */
export function sanitizeHTML(input) {
  if (typeof input !== 'string') {
    return input;
  }
  
  return input
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;')
    .replace(/\//g, '&#x2F;');
}

/**
 * Sanitize user input for display purposes
 * @param {string} input - User input to sanitize
 * @returns {string} - Sanitized input
 */
export function sanitizeUserInput(input) {
  if (typeof input !== 'string') {
    return input;
  }
  
  const original = input;
  
  // Remove potential XSS patterns
  let result = input
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    .replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, '')
    .replace(/javascript:/gi, '')
    .replace(/on\w+\s*=/gi, '')
    .replace(/data:text\/html/gi, '')
    .trim();
  
  // Return original string reference if no changes were made
  // This prevents React from resetting cursor position unnecessarily
  return result === original ? original : result;
}

/**
 * Validate that a token has the expected format
 * @param {string} token - JWT token to validate
 * @returns {boolean} - True if token format is valid
 */
export function validateTokenFormat(token) {
  if (!token || typeof token !== 'string') {
    return false;
  }
  
  // Basic JWT format validation (3 parts separated by dots)
  const parts = token.split('.');
  if (parts.length !== 3) {
    return false;
  }
  
  // Check for suspicious patterns
  if (token.includes('<script') || token.includes('javascript:')) {
    return false;
  }
  
  return true;
}

/**
 * Sanitize URL to prevent malicious redirects
 * @param {string} url - URL to sanitize
 * @returns {string|null} - Sanitized URL or null if invalid
 */
export function sanitizeURL(url) {
  if (!url || typeof url !== 'string') {
    return null;
  }
  
  // Allow only HTTP/HTTPS and relative URLs
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('/')) {
    // Remove potential XSS in URLs
    const sanitized = url.replace(/javascript:/gi, '').replace(/data:/gi, '');
    return sanitized;
  }
  
  // Allow relative paths
  if (!url.includes(':')) {
    return url;
  }
  
  return null;
}

/**
 * Rate limiting helper for preventing abuse
 */
class RateLimiter {
  constructor(maxAttempts = 5, windowMs = 15 * 60 * 1000) { // 5 attempts per 15 minutes
    this.attempts = new Map();
    this.maxAttempts = maxAttempts;
    this.windowMs = windowMs;
  }
  
  isAllowed(identifier) {
    const now = Date.now();
    const userAttempts = this.attempts.get(identifier) || [];
    
    // Remove old attempts outside the window
    const validAttempts = userAttempts.filter(time => now - time < this.windowMs);
    
    if (validAttempts.length >= this.maxAttempts) {
      return false;
    }
    
    // Add current attempt
    validAttempts.push(now);
    this.attempts.set(identifier, validAttempts);
    
    return true;
  }
  
  reset(identifier) {
    this.attempts.delete(identifier);
  }
}

export const loginRateLimiter = new RateLimiter(5, 15 * 60 * 1000); // 5 login attempts per 15 minutes

/**
 * Token refresh manager for automatic token renewal
 */
class TokenManager {
  constructor() {
    this.refreshTimer = null;
    this.isRefreshing = false;
  }
  
  /**
   * Start automatic token refresh
   * @param {number} refreshIntervalMs - Refresh interval in milliseconds
   */
  startAutoRefresh(refreshIntervalMs = 90 * 60 * 1000) { // 90 minutes
    this.stopAutoRefresh();
    
    this.refreshTimer = setInterval(async () => {
      await this.refreshToken();
    }, refreshIntervalMs);
  }
  
  /**
   * Stop automatic token refresh
   */
  stopAutoRefresh() {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
      this.refreshTimer = null;
    }
  }
  
  /**
   * Refresh the authentication token
   */
  async refreshToken() {
    if (this.isRefreshing) {
      return;
    }
    
    this.isRefreshing = true;
    
    try {
      // Import here to avoid circular dependency
      const { getToken } = await import('../Requests/User/UserRequests');
      const currentToken = getToken();
      
      if (!currentToken) {
        this.stopAutoRefresh();
        return;
      }
      
      // Call refresh endpoint (you'll need to implement this on your backend)
      const response = await fetch(process.env.REACT_APP_API_BASE_URL + '/refresh-token', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${currentToken}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        const { setSecureCookie } = await import('./cookies');
        
        // Update tokens with new values
        setSecureCookie('token', data.token);
        if (data.user_id) setSecureCookie('user_id', data.user_id);
        if (data.team_uuid) setSecureCookie('team_uuid', data.team_uuid);
        if (data.credits) setSecureCookie('credits', data.credits);
        
        // Token refreshed successfully
      } else {
        console.warn('Token refresh failed, stopping auto-refresh');
        this.stopAutoRefresh();
        // Optionally redirect to login
        window.location.href = '/login';
      }
    } catch (error) {
      console.error('Token refresh error:', error);
    } finally {
      this.isRefreshing = false;
    }
  }
}

export const tokenManager = new TokenManager();

/**
 * Detect suspicious activity patterns
 * @param {string} userAgent - User agent string
 * @param {string} ip - IP address (if available)
 * @returns {boolean} - True if activity seems suspicious
 */
export function detectSuspiciousActivity(userAgent = '', ip = '') {
  const suspiciousPatterns = [
    /bot|crawler|spider|scraper/i,
    /script|eval|alert|prompt|confirm/i,
    /<script|javascript:|data:text\/html/i
  ];
  
  return suspiciousPatterns.some(pattern => 
    pattern.test(userAgent) || pattern.test(ip)
  );
}